import React, { useState, useRef, useImperativeHandle, useEffect } from 'react';
import { dynamicStyles } from './Comments.styles';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { getJWtDetails } from '../../utils/AppUtils';
import { Box, InputAdornment, Stack, Typography } from '@mui/material';
import CustomButton from '../Button/CustomButton';
import { FONT_ENUM } from '../../types/global/fonts.types';
import CloseIcon from '../CustomSVGIcons/CloseIcon';
import SendIcon from '../CustomSVGIcons/SendIcon';
import { CustomTextField } from '../TextInput/CustomTextField';
import { useDispatch } from 'react-redux';
import { toggleAuthModal } from '../../redux/authentication/action';

interface IProps {
    onCommentSend: (arg: string) => void;
    placeHolder?: string;
    defaultValue?: string;
    showClear?: boolean;
    onCloseClick?: () => void;
    commentDisabled?: boolean;
}

const CommentInput = React.forwardRef((props: IProps, ref: any) => {
    const { onCommentSend, defaultValue, showClear, onCloseClick, commentDisabled } = props;
    const { themeStyle } = useThemeContext();
    const [value, setValue] = useState('');
    const textInputRef: any = useRef(null);
    const { isSignedIn } = getJWtDetails();
    const dispatch = useDispatch();

    const onChangeText = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setValue(e.target.value);
    };

    const onSendComment = () => {
        onCommentSend(value);
        setValue('');
        textInputRef.current?.blur();
    };

    const handleKeyDown = (event: any) => {
        if (event.key === 'Enter') {
            onSendComment();
        }
    };

    const isDisabled = () => {
        if (/\S/.test(value)) {
            return false;
        } else {
            return true;
        }
    };
    const styles = dynamicStyles(themeStyle, isDisabled());

    useImperativeHandle(ref, () => ({
        focus() {
            textInputRef.current?.focus();
        },
    }));

    useEffect(() => {
        if (defaultValue) {
            setValue(defaultValue);
            // Auto-focus when editing (when defaultValue is provided)
            // Multiple attempts for better mobile compatibility
            const focusInput = () => {
                if (textInputRef.current) {
                    textInputRef.current.focus();
                    // For mobile devices, also trigger click to ensure keyboard appears
                    if ('ontouchstart' in window) {
                        textInputRef.current.click?.();
                    }
                }
            };

            setTimeout(focusInput, 50);
            setTimeout(focusInput, 150);
            setTimeout(focusInput, 300);
        }
    }, [defaultValue]);

    const signInNotification = (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            sx={{
                position: 'relative',
                backgroundColor: theme[themeStyle].form_gray,
                padding: '15px',
                borderRadius: 4,
                border: `1px solid ${theme[themeStyle].tag_white}`,
            }}>
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.REGULAR_HEADER,
                    color: theme[themeStyle].tag_white,
                    fontSize: 12,
                }}>
                Sign In to leave a comment
            </Typography>
            <CustomButton
                label="Sign In"
                onClick={() => dispatch(toggleAuthModal(true))}
                sx={{ mx: 1 }}
                textStyle={{ fontSize: 12 }}
                customStyle={{ height: 20 }}
            />
        </Stack>
    );

    if (commentDisabled) {
        return (
            <Box
                sx={{
                    backgroundColor: theme[themeStyle].form_gray,
                    padding: '15px',
                    borderRadius: 2,
                    border: `1px solid ${theme[themeStyle].tag_white}`,
                    textAlign: 'center',
                }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        color: theme[themeStyle].tag_white,
                        fontSize: 12,
                    }}>
                    Comments have been restricted
                </Typography>
            </Box>
        );
    }

    return !isSignedIn ? (
        signInNotification
    ) : (
        <Box>
            {showClear && (
                <CloseIcon onClick={onCloseClick} stroke={theme[themeStyle].tag_white} style={{ cursor: 'pointer' }} />
            )}
            <Box style={styles.commentTextInputContainer}>
                <CustomTextField
                    mainSx={{
                        borderWidth: 1.5,
                    }}
                    textFieldProps={{
                        ref: textInputRef,
                        multiline: true,
                        rows: 3,
                        type: 'text',
                        onChange: onChangeText,
                        onKeyDown: handleKeyDown,
                        sx: {
                            '& .MuiInputBase-input::placeholder': {
                                fontSize: 12, // Adjust placeholder font size
                                color: theme[themeStyle].darkGray, // Customize placeholder color
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            },
                        },
                        style: {
                            width: '100%',
                            paddingLeft: 14,
                            paddingRight: 14,
                            backgroundColor: theme[themeStyle].gray_text,
                            borderRadius: 8,
                            border: 'none',
                        },
                        InputProps: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <SendIcon
                                        onClick={onSendComment}
                                        style={{
                                            justifyContent: 'center',
                                            marginLeft: 8,
                                            opacity: isDisabled() ? 0.3 : 1,
                                            paddingRight: 10,
                                            transition: 'display 0.3s ease-in-out',
                                            cursor: 'pointer',
                                        }}
                                        height={'50px'}
                                        width={'50px'}
                                        fill={isDisabled() ? theme[themeStyle].darkGray60 : theme[themeStyle].highlight}
                                    />
                                </InputAdornment>
                            ),
                            disableUnderline: true,
                            placeholder: 'Leave a comment...',
                            value: value,
                            sx: {
                                fontSize: 12,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            },
                        },
                    }}
                />
            </Box>
        </Box>
    );
});

CommentInput.defaultProps = {
    placeHolder: 'Leave a comment...',
};

export { CommentInput };
